import { ColDef, ICellRendererParams } from '@ag-grid-community/core';

import { AgRawTooltipParams } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { PILL_STYLES_BY_INDEX } from '@shared/constants/ag-classes';
import { ZERO_HYPHEN } from '@shared/constants/format';
import { TableConstants } from '@shared/constants/table.constants';
import { GlobalState } from '@shared/store/global/global.model';
import { agWidthOfAColumn } from '@shared/utils/ag-width-of-a-column';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';

export type AttributeConfig<T> = Pick<
  GlobalState,
  'attributes' | 'primaryAttribute' | 'secondaryAttribute'
> & {
  gridData: T[];
};

export const rawAttributeColumnRenderer = <T>(
  attributeConfig: Pick<AttributeConfig<T>, 'gridData'>,
  name: string,
  index: number,
  widthConfig: {
    maxWidth?: number;
    minWidth?: number;
    offset?: number;
  } = {
    maxWidth: 200,
    minWidth: 70,
    offset: 50,
  }
) => {
  const length = Object.keys(PILL_STYLES_BY_INDEX).length;

  return {
    cellRenderer: (params: ICellRendererParams) => {
      const value = params.value;

      if (!value || params.node.isRowPinned()) {
        return ZERO_HYPHEN;
      }

      const eDiv = document.createElement('div');
      const classList = [
        'leading-[23px]',
        'px-[10px]',
        'rounded',
        'text-[14px]',
        'truncate',
        'h-full',
        'max-w-[max-content]',
        ...(PILL_STYLES_BY_INDEX[index % length] || []),
      ];

      eDiv.classList.add(...classList);
      eDiv.innerText = value;

      return eDiv;
    },
    cellStyle: (params) => {
      if (params.value) {
        return {
          '--ag-cell-vertical-padding': 'calc((var(--ag-row-height) - 25px) / 2)',
        };
      }
    },
    minWidth: TableConstants.ATTRIBUTE_CELL_MIN_WIDTH,
    tooltipComponentParams: <AgRawTooltipParams>{
      getNode: ({ targetNode }) => targetNode?.querySelector('div'),
    },
    width: agWidthOfAColumn({
      field: getEncodedAttributeName(name),
      gridData: attributeConfig.gridData,
      headerName: name,
      ...widthConfig,
    }),
  } satisfies ColDef;
};
